import * as Analytics from "./AnalyticsBeacons";
import {fetch, FetchOptions} from "../../utils/fetch";
import {inject} from "../di";
import {ConfigService} from "../ConfigService";
import {FlyerService} from "../FlyerService";
import detectUserEnv from "../../utils/detectUserEnv";
import {initSessionStorageSequenceId, getSessionStorageSequenceId, increaseSequenceId} from "../../utils/SessionStorageSequenceId";

// probably this services do not defined in moment of initialization of this component
import {AnalyticsService} from "../AnalyticsService";

const userEnv: {
	browser: string,
	version: string,
	mobile: number,
	os: string,
	osversion: string
} = detectUserEnv();

interface Options { [key: string]: any; }

interface AvroAnalyticsChannelInterface {
	FireHeroPostFlyerViewFlyerBeacon(options: Options): void;
	FireHeroPostFlyerEngagedVisitFlyerBeacon(options: Options): void;
	FireHeroPostFlyerClickFlyerItemBeacon(options: Options): void;
	FireHeroPostClosePostBeacon(options: Options): void;
	FireHeroPostOpenFlyerBeacon(options: Options): void;
	FireHeroPostOpenPostExperienceBeacon(options: Options): void;
}

export default class AvroAnalyticsChannel implements AvroAnalyticsChannelInterface {
	defaultString: string = "FlippAvroDefault";
	defaultEnumType: null = null;
	defaultNumber: number = -1;
	testChannelIds: number[] = [3280, 3182, 3179, 3138, 3127];

	// budget id will be set after promise is resolved in getBudget() func
	budgetId: number = this.defaultNumber;

	@inject flyerService: FlyerService;
	@inject config: ConfigService;

	// injection of this service does not work in this class with AnalyticsService
	private analyticsService: AnalyticsService = AnalyticsService.getInstance();

	constructor() {
		initSessionStorageSequenceId();
	}

	public FireHeroPostFlyerViewFlyerBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostFlyerViewFlyerBeacon(Analytics.Schemas.HeroPostFlyerViewFlyer, options));
	}

	public FireHeroPostFlyerEngagedVisitFlyerBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostFlyerEngagedVisitFlyerBeacon(Analytics.Schemas.HeroPostFlyerEngagedVisitFlyer, options));
	}

	public FireHeroPostFlyerClickFlyerItemBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostFlyerClickFlyerItemBeacon(Analytics.Schemas.HeroPostFlyerClickFlyerItem, options));
	}

	public FireHeroPostClosePostBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostClosePostBeacon(Analytics.Schemas.HeroPostClosePost, options));
	}

	public FireHeroPostOpenFlyerBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostOpenFlyerBeacon(Analytics.Schemas.HeroPostOpenFlyer, options));
	}

	public FireHeroPostOpenPostExperienceBeacon(options: Options) {
		this.FireAvroBeacon(this.HeroPostOpenPostExperienceBeacon(Analytics.Schemas.HeroPostOpenPostExperience, options));
	}

	private HeroPostFlyerViewFlyerBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostFlyerViewFlyer {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			flyer: this.getFlyer(options),
			merchant: this.getMerchant(options),
			heroBase: this.getHeroBase(),
			viewParameters: this.getViewParameters(options),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	private HeroPostFlyerEngagedVisitFlyerBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostFlyerEngagedVisitFlyer {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			flyer: this.getFlyer(options),
			merchant: this.getMerchant(options),
			heroBase: this.getHeroBase(),
			repeat: Boolean(Number(options.repeat)),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	private HeroPostFlyerClickFlyerItemBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostFlyerClickFlyerItem {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			flyer: this.getFlyer(options),
			flyerItem: this.getFlyerItem(options),
			merchant: this.getMerchant(options),
			heroBase: this.getHeroBase(),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	private HeroPostClosePostBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostClosePost {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			flyer: this.getFlyer(options),
			merchant: this.getMerchant(options),
			heroBase: this.getHeroBase(),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	private HeroPostOpenFlyerBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostOpenFlyer {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			flyer: this.getFlyer(options),
			merchant: this.getMerchant(options),
			sourceAction: this.getSourceAction(options),
			heroBase: this.getHeroBase(),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	private HeroPostOpenPostExperienceBeacon(
			schema: Analytics.Schemas,
			options: Options
	): Analytics.Beacon.HeroPostOpenPostExperience {
		return {
			base: this.getBase(),
			budget: this.getBudget(options),
			publisher: this.getPublisher(),
			heroBase: this.getHeroBase(),
			schemaInfo: this.getSchemaInfo(schema)
		};
	}

	// duplicated this method from locationService, coz cannot inject it and cannot getInstance of it in this class
	private postalCode() {
		return document.documentElement.getAttribute("postalcode");
	}

	private getBase(): Analytics.Base {
		return {
			aid: Analytics.ApplicationID.Hero,
			postalCode: { string: this.postalCode() },
			rnd: Math.random().toString()
		};
	}

	private getSchemaInfo(schema: Analytics.Schemas): Analytics.SchemaInfo {
		return {
			schemaId: schema
		};
	}

	private getBudget(options: Options): Analytics.Budget {
		this.flyerService.getFullFlyer(options.flyer_id)
				.then(flyerDetails => {
					this.budgetId = flyerDetails && flyerDetails.flyer && flyerDetails.flyer.budgetId ? flyerDetails.flyer.budgetId : this.defaultNumber;
				}).catch();

		return {
			budgetId: this.budgetId,
			costModelType: this.defaultEnumType
		};
	}

	private getMerchant(options: Options): Analytics.Merchant {
		return {
			merchantId: Number(options.merchant_id)
		};
	}

	private getSourceAction(options: Options): Analytics.SourceAction {
		switch (options.source_action) {
			case "cross_browse":
				return Analytics.SourceAction.CrossBrowse;
			case "direct":
			default:
				return Analytics.SourceAction.Direct;
		}
	}

	private getFlyer(options: Options): Analytics.Flyer {
		return {
			flyerId: Number(options.flyer_id),
			flyerRunId: Number(options.flyer_run_id),
			flyerTypeId: Number(options.flyer_type_id),
			premium: JSON.parse(options.premium_merchant) // options.premium_merchant can be sting "true" of string "false", JSON.parse() - make it boolean
		};
	}

	private getFlyerItem(options: Options): Analytics.FlyerItem {
		return {
			id: Number(options.flyer_item_id)
		};
	}
	private getPublisher(): Analytics.Publisher {
		const publisherId = parseInt(this.config.publisherId);

		return {
			publisherId: publisherId ? publisherId : this.defaultNumber,
			// read readme.md chapter '!important! error in "@flipp/avro-typescript-cli"'
			// @ts-ignore, here should be ts-ignore
			channelId: { "long": Number(this.analyticsService.channelId) }
		};
	}

	private getClientParameters(): Analytics.ClientParameters {
		return {
			browser: userEnv.browser || this.defaultString,
			browserVersion: userEnv.version || this.defaultString,
			system: userEnv.os || (userEnv.mobile ? "mobile" : this.defaultString),
			systemModel: this.defaultString,
			systemVersion:  userEnv.osversion || this.defaultString
		};
	}

	private getHeroCampaign (): Analytics.HeroCampaign {
		const moduleId = parseInt(this.config.moduleId);
		const campaignId = parseInt(this.config.campaignId);

		return {
			moduleId: moduleId ? moduleId : this.defaultNumber,
			campaignId: campaignId ? campaignId : this.defaultNumber
		};
	}

	private getEnvironment(): Analytics.Environment {
		const channelId = Number(this.config.channelId);

		switch (this.config.ENV) {
			case "development":
				return Analytics.Environment.Dev;
			case "production":
				// workaround for our production native-modules that hosted on staging s3 bucket http://s3.amazonaws.com/flipp-module/test-module3.0.html
				if (this.testChannelIds.includes(channelId)) return Analytics.Environment.Test;

				return Analytics.Environment.Prod;
			case "staging":
				return Analytics.Environment.Test;
			default:
				return Analytics.Environment.Dev;
		}
	}

	private getLocationSource(): Analytics.LocationSource {
		// TODO inject distAnalytics here, create method locationSource, get data from url like in _buildAnalyticsParameters, use enum LocationSource and creacte from it enum appropriate to use in avro (numbers value to string value). I"m not sure will it work coz we use method after dist analytics is finished and we dont have origin url from module. if it would not work save data from this._buildAnalyticsParameters() in dist analytics in constructor, in that case we can take param from this class (i probably will work)
		return Analytics.LocationSource.Default;
	}

	private getHeroBase(): Analytics.HeroBase {
		increaseSequenceId();

		return {
			environment: this.getEnvironment(),
			clientParameters: this.getClientParameters(),
			LocationSource: this.getLocationSource(),
			epochMilliseconds: new Date().getTime(),
			sessionId: this.analyticsService.sessionId,
			sequenceId: getSessionStorageSequenceId(),
			appVersion: this.config.BUILD,
			heroCampaign: this.getHeroCampaign()
		};
	}

	private getViewParameters(options: Options): Analytics.ViewParameters {
		return {
			left: Number(options.left),
			right: Number(options.right),
			top: Number(options.top),
			bottom: Number(options.bottom),
			resolution: Number(options.resolution),
			userScreenHeight: Number(options.userScreenHeight),
			userScreenWidth: Number(options.userScreenWidth),
			// devicePixelRatio should have type LONG
			// default value is 1, thus we cannot send < 1
			devicePixelRatio: window.devicePixelRatio > 1 ? Number(window.devicePixelRatio.toFixed()) : 1
		};
	}

	private FireAvroBeacon(data: any) {
			const opts: FetchOptions = {
				url: this.config.AVRO_BEACONS,
				method: "POST",
				headers: [
					{"Content-Type": "text/plain; charset=UTF-8"},
				],
				body: encodeURI(JSON.stringify([data]))
			};

			fetch(opts);
	}
}
